import React, { useLayoutEffect, useRef } from "react";
import { fmtDateTime, timeAgo } from "../../../ForBrands/brandDealUtils";
import iconPaperclip from "../../../../assets/brand/icon-paperclip.svg";
import { Avatar, Badge, ErrorState, Loading } from "../../../ForBrands/ui/BrandUI";
import { MobileStackHeader } from "../../../ForBrands/ui/mobile/MobileShell";
import { ContextStrip, ListRule, MobileCard, MobileEmpty, MobilePageHead, TextLink } from "../../../ForBrands/ui/mobile/MobileUI";
import { HELP_ACTION } from "../../../ForBrands/ui/mobile/support";

// Athlete Mode · Messages on phones (Figma "09 · Messages, Inbox" 2000:1055
// and "09a · Messages, Thread" 2000:1077). Both are pushed screens with their
// own stack header. The inbox is a screen of its own (nothing is picked for
// the athlete, D-a23); tapping a conversation opens the thread screen, a
// full-height column whose message list is the only inner scroller, with the
// composer pinned under it (D-a22 keeps the paperclip). The athlete's own
// messages sit on the right on grey, the brand's on the left on white
// (D-a21). AthleteMessagesPage owns the data, polling, sending and the
// per-conversation drafts.

const cx = (...parts) => parts.filter(Boolean).join(" ");

const INBOX_NOTE = "Messages are delivered in-app. You also get an email notification for every new message from a brand.";
const THREAD_NOTE = "Messages are delivered in-app. Brands also get an email notification for every new message.";

// ------------------------------------------------------------------ Inbox
// One conversation row (2026:1512): the company's initials (or its avatar),
// the company with "New" and the time, the campaign, then the preview.
const ThreadRow = ({ thread, onOpen }) => {
  const company = thread.brand?.company || "Brand";
  const unread = thread.unread > 0;
  return (
    <button type="button" onClick={() => onOpen(thread.id)} className="flex w-full items-start gap-3 py-[14px] text-left">
      <Avatar name={company} src={thread.brand?.avatar || undefined} size={40} tone="muted" square className="!font-semibold !text-[12px]" />
      <span className="flex min-w-0 flex-1 flex-col gap-1 break-words">
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 bm-h-xs text-bm-ink">{company}</span>
          {unread && (
            <Badge tone="brand" className="shrink-0">
              New
            </Badge>
          )}
          {thread.lastAt && (
            <span className="shrink-0 whitespace-nowrap bm-body-xs text-bm-muted">{timeAgo(thread.lastAt, { short: true })} ago</span>
          )}
        </span>
        {thread.campaignName && <span className="bm-body-xs text-bm-muted">{thread.campaignName}</span>}
        <span className={cx("line-clamp-2 bm-body-s", unread ? "text-bm-ink" : "text-bm-muted")}>{thread.preview || "No messages yet"}</span>
      </span>
    </button>
  );
};

export const InboxMobile = ({ threads, rep, error, loaded, onOpen, onBack }) => (
  <div className="flex flex-col">
    <MobileStackHeader title="Messages" leading="back" onLeading={onBack} action={HELP_ACTION} />
    <div className="flex flex-col gap-4 px-6 pt-5 pb-8">
      <MobilePageHead
        eyebrow="Deal flow"
        eyebrowBrand
        title="Messages"
        sub={`All your brand conversations${rep ? ` · ${rep.name} (your rep) has visibility on every thread` : ""}`}
      />
      {error && <ErrorState>{error}</ErrorState>}
      <p className="bm-overline">Conversations</p>
      {!loaded ? (
        <Loading />
      ) : threads.length === 0 ? (
        <MobileEmpty>No conversations yet. Asking a question on an offer or messaging a brand from My Deals starts one.</MobileEmpty>
      ) : (
        <MobileCard density="list">
          {threads.map((t, i) => (
            <React.Fragment key={t.id}>
              {i > 0 && <ListRule />}
              <ThreadRow thread={t} onOpen={onOpen} />
            </React.Fragment>
          ))}
        </MobileCard>
      )}
      <p className="bm-body-xs text-bm-muted">{INBOX_NOTE}</p>
    </div>
  </div>
);

// ----------------------------------------------------------------- Thread
// A message (2026:1552 outgoing, 2026:1556 incoming).
const BubbleMobile = ({ message, brandName }) => {
  const mine = message.from === "athlete";
  return (
    <div className={cx("flex flex-col gap-1", mine ? "items-end" : "items-start")}>
      <div
        className={cx(
          "max-w-[80%] break-words rounded-[16px] px-[14px] py-3 bm-body-m text-bm-ink",
          mine ? "bg-bm-mutedbg" : "border border-bm-border bg-white",
        )}
      >
        {message.text}
        {message.attachment?.name && (
          <div className="mt-2 inline-flex max-w-full items-center gap-2 rounded-full border border-bm-border bg-white px-3 py-1 bm-label-xs text-bm-ink">
            <img src={iconPaperclip} alt="" className="h-3 w-3 shrink-0" />
            <span className="min-w-0 truncate">{message.attachment.name}</span>
          </div>
        )}
      </div>
      <span className="bm-body-xs text-bm-muted">
        {mine ? "You" : brandName} · {fmtDateTime(message.at)}
      </span>
    </div>
  );
};

// `thread` is the loaded conversation for the open id, or null while it
// loads (`threadError` when it could not be opened). `title` is the brand's
// company, known from the inbox list before the thread arrives.
export const ThreadMobile = ({
  title,
  thread,
  threadError,
  rep,
  dealAction,
  text,
  onTextChange,
  onSubmit,
  sending,
  attachment,
  onRemoveAttachment,
  fileRef,
  onPickFile,
  onBack,
}) => {
  const listRef = useRef(null);
  const brandName = thread?.brand?.company || "Brand";
  const count = thread?.messages?.length || 0;

  // Open at the newest message, and follow new ones.
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [thread?.id, count]);

  const context = thread
    ? [thread.campaign?.name, thread.brand?.fullName ? `${thread.brand.fullName}, brand contact` : null, rep ? "your rep can see this thread" : null]
        .filter(Boolean)
        .join(" · ")
    : "";

  // Browsers without dvh units (iOS Safari before 15.4) ignore h-dvh; the
  // @supports fallback gives them a 100vh column instead, so the composer
  // stays pinned. (Listing h-screen next to h-dvh would not work: Tailwind
  // emits .h-screen after .h-dvh, so 100vh would win everywhere.)
  // Figma draws no visible heading; the brand's name is the screen's heading
  // for screen readers (the header shows it as its title).
  return (
    <div className="flex h-dvh flex-col supports-[not(height:100dvh)]:h-screen">
      <MobileStackHeader title={title} leading="back" onLeading={onBack} action={HELP_ACTION} />
      <h1 className="sr-only">{title || "Messages"}</h1>
      {thread && (context || dealAction) && (
        <ContextStrip>
          {context && <p className="bm-body-s text-bm-ink">{context}</p>}
          {dealAction && (
            <TextLink to={dealAction.href} className="self-start">
              View deal details
            </TextLink>
          )}
        </ContextStrip>
      )}

      <div ref={listRef} className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto overscroll-contain px-6 pt-5 pb-8">
        {!thread ? (
          threadError ? <ErrorState>{threadError}</ErrorState> : <Loading />
        ) : (
          <>
            {count ? (
              thread.messages.map((m) => <BubbleMobile key={m.id} message={m} brandName={thread.brand?.fullName || brandName} />)
            ) : (
              <p className="bm-body-m text-bm-muted">Say hello to start the conversation.</p>
            )}
            <p className="text-center bm-body-xs text-bm-muted">{THREAD_NOTE}</p>
          </>
        )}
      </div>

      {thread && (
        <div className="flex shrink-0 flex-col gap-2 border-t border-bm-mutedbg bg-white px-6 pt-[14px] pb-[max(28px,env(safe-area-inset-bottom))]">
          {attachment && (
            <span className="flex min-w-0 items-center gap-2 bm-body-s text-bm-muted">
              <span className="min-w-0 truncate">Attaching {attachment.name}</span>
              <button type="button" onClick={onRemoveAttachment} className="shrink-0 text-bm-critical">
                remove
              </button>
            </span>
          )}
          <div className="flex items-center gap-[10px]">
            <div className="relative min-w-0 flex-1">
              <input
                value={text}
                onChange={(e) => onTextChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), onSubmit())}
                placeholder={`Reply to ${thread.brand?.firstName || brandName}...`}
                enterKeyHint="send"
                className="bm-input-lg !pr-[52px]"
              />
              <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                aria-label="Attach a file"
                title={attachment ? attachment.name : "Attach a file"}
                className={cx(
                  "absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full",
                  attachment ? "bg-bm-subtle ring-1 ring-bm-orange" : "hover:bg-bm-subtle",
                )}
              >
                <img src={iconPaperclip} alt="" className="h-5 w-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={onSubmit}
              disabled={sending || (!text.trim() && !attachment)}
              className="flex h-14 w-[88px] shrink-0 items-center justify-center rounded-full bg-bm-ink bm-label-l text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:bg-bm-border disabled:text-bm-muted"
            >
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
